import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import { Badge } from "../../components/ui/badge";
import { WeCockpitLogo } from "../../components/branding/WeCockpitLogo";
import { CheckCircle2, ArrowRight, ArrowLeft, Euro, Calendar, CreditCard, Banknote, FileCheck2, Sparkles, Tag, X, Rocket, PartyPopper, Trophy, FileText, Check, Mail } from "lucide-react";
import { toast } from "sonner";

const cents = (v) => (Number(v || 0) / 100).toFixed(0) + " €";

// Messages d'encouragement par étape (gamification)
// (déclaré localement dans le composant pour utiliser isCustomQuote)

export default function OnboardingRequestPage() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [plans, setPlans] = useState([]);
  const [widgets, setWidgets] = useState([]);
  const [cgv, setCgv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [draftToken, setDraftToken] = useState(null);
  const [agencySettings, setAgencySettings] = useState({});

  // Code promo
  const [promoInput, setPromoInput] = useState("");
  const [promoApplied, setPromoApplied] = useState(null); // {code, discount_percent, description, is_internal}
  const [promoChecking, setPromoChecking] = useState(false);

  const checkPromo = async () => {
    const c = (promoInput || "").trim().toUpperCase();
    if (!c) return;
    setPromoChecking(true);
    try {
      const { data } = await api.post("/agency/promo-codes/validate", { code: c });
      setPromoApplied(data);
      toast.success(`Code ${c} accepté · -${data.discount_percent}%`);
    } catch (e) {
      setPromoApplied(null);
      toast.error(e?.response?.data?.detail || "Code invalide");
    } finally {
      setPromoChecking(false);
    }
  };
  const removePromo = () => {
    setPromoApplied(null);
    setPromoInput("");
  };

  // Sprint F2 — Reprise d'un brouillon via ?draft=token
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("draft");
    if (t) {
      api.get(`/agency/onboarding/draft/${t}`)
        .then(r => {
          const d = r.data;
          setDraftToken(t);
          setForm(prev => ({ ...prev, ...d, draft_token: undefined }));
          if (d.last_step_completed >= 1) setStep(Math.min(4, d.last_step_completed + 1));
        })
        .catch(() => {});
    }
  }, []);

  // Sprint F2 — Autosave après chaque step transition
  const saveDraft = async (currentStep) => {
    try {
      const payload = { ...form, draft_token: draftToken, last_step_completed: currentStep };
      const r = await api.post("/agency/onboarding/draft", payload);
      if (!draftToken && r.data?.draft_token) setDraftToken(r.data.draft_token);
    } catch { /* silent */ }
  };

  const [form, setForm] = useState({
    plan_code: "",
    extra_widgets: [],
    company_name: "",
    contact_first_name: "",
    contact_last_name: "",
    contact_email: "",
    contact_phone: "",
    billing_address: "",
    billing_zip: "",
    billing_city: "",
    siret: "",
    sector_preset: "",
    notes: "",
    setup_payment_method: "stripe",
    setup_installments: 1,
    cgv_accepted: false,
    signed_name: "",
  });

  useEffect(() => {
    Promise.all([
      api.get("/agency/public/plans"),
      api.get("/agency/public/widgets"),
      api.get("/agency/public/cgv/current"),
      api.get("/settings"),
    ]).then(([p, w, c, s]) => {
      setPlans(p.data); setWidgets(w.data); setCgv(c.data);
      setAgencySettings(s.data || {});
      if (p.data.length > 0) setForm(f => ({ ...f, plan_code: p.data[0].code }));
      setLoading(false);
    }).catch(() => { setLoading(false); toast.error("Erreur chargement forfaits"); });
  }, []);

  // Lire ?preset=vitrine|reservation|boutique|location|hybride|custom dans l'URL
  // Pré-configure le forfait ET pré-coche les widgets pertinents selon le type de site choisi
  const [isCustomQuote, setIsCustomQuote] = useState(false);
  const [presetLabel, setPresetLabel] = useState("");
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const preset = params.get("preset");
    const mode = params.get("mode");
    if (preset === "custom" || mode === "quote") {
      setIsCustomQuote(true);
    }
    // Mapping enrichi : { plan_code, extra_widgets[], label }
    const presetConfig = {
      vitrine:     { plan: "vitrine", widgets: [],                                                          label: "Site Vitrine" },
      reservation: { plan: "starter", widgets: ["booking_enabled"],                                         label: "Site avec Réservation" },
      boutique:    { plan: "pro",     widgets: ["shop_click_collect_enabled"],                              label: "Site Boutique" },
      location:    { plan: "pro",     widgets: ["rental_enabled"],                                          label: "Site avec Location" },
      hybride:     { plan: "premium", widgets: ["shop_click_collect_enabled", "food_mode_enabled", "b2b_enabled", "loyalty_enabled"], label: "Solution Hybride complète" },
      custom:      { plan: "premium", widgets: [],                                                          label: "Projet sur-mesure" },
    };
    if (preset && presetConfig[preset] && plans.length > 0) {
      const cfg = presetConfig[preset];
      setPresetLabel(cfg.label);
      const targetPlan = plans.find(p => p.code === cfg.plan);
      if (targetPlan) {
        // Filtre les widgets : ne pré-coche que ceux qui ne sont PAS déjà inclus dans le plan
        const includedSet = new Set(targetPlan.included_widgets || []);
        const extraToCheck = cfg.widgets.filter(w => !includedSet.has(w));
        setForm(f => ({ ...f, plan_code: cfg.plan, extra_widgets: extraToCheck }));
      }
    }
  }, [plans]); // eslint-disable-line

  const selectedPlan = plans.find(p => p.code === form.plan_code);

  const totalExtra = form.extra_widgets.reduce((sum, key) => {
    const w = widgets.find(x => x.widget_key === key);
    if (!w) return sum;
    const base = w.monthly_price_cents || 0;
    const discounted = w.discount_pct > 0 ? base * (1 - w.discount_pct / 100) : base;
    return sum + discounted;
  }, 0);

  const next = () => {
    if (step === 1 && !form.plan_code) return toast.error("Choisis un forfait");
    if (step === 2) {
      if (!form.company_name || !form.contact_email || !form.contact_first_name || !form.contact_phone) {
        return toast.error("Tous les champs obligatoires doivent être remplis");
      }
    }
    if (step === 3) {
      if (!form.cgv_accepted) return toast.error("Tu dois accepter les CGV");
      if (!form.signed_name.trim()) return toast.error("Signe en tapant ton nom");
    }
    saveDraft(step);                  // Sprint F2 : autosave à chaque transition
    setStep(step + 1);
  };
  const prev = () => setStep(Math.max(1, step - 1));

  const submit = async () => {
    if (!form.cgv_accepted || !form.signed_name.trim()) {
      toast.error("Acceptation CGV + signature requises");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        company_name: form.company_name,
        contact_first_name: form.contact_first_name,
        contact_last_name: form.contact_last_name,
        contact_email: form.contact_email,
        contact_phone: form.contact_phone,
        billing_address: form.billing_address,
        billing_zip: form.billing_zip,
        billing_city: form.billing_city,
        siret: form.siret,
        plan_code: form.plan_code,
        sector_preset: form.sector_preset,
        extra_widgets: form.extra_widgets,
        notes: form.notes,
        setup_payment_method: form.setup_payment_method,
        setup_installments: form.setup_installments,
        cgv_version: cgv.version,
        signed_at: new Date().toISOString(),
        signed_name: form.signed_name,
        draft_token: draftToken,
      };
      const r = await api.post("/agency/onboarding/submit", payload);
      let request = r.data;

      // Application du code promo si saisi
      if (promoApplied?.code) {
        try {
          const promoRes = await api.post("/agency/onboarding/apply-promo", {
            onboarding_request_id: request.id,
            code: promoApplied.code,
          });
          // Si 100% gratuit → on saute Stripe, on affiche directement la confirmation
          if (promoRes.data?.is_free) {
            request = { ...request, setup_fee_total_cents: 0, setup_payment_method: "promo_free", status: "paid_setup", promo_code_applied: promoApplied.code };
            setSubmitted(request);
            setStep(5);
            return;
          }
          // Sinon on met à jour le total local pour la suite Stripe
          request = { ...request, setup_fee_total_cents: promoRes.data.new_total_cents };
        } catch (e) {
          toast.error("Code promo : " + (e?.response?.data?.detail || e.message));
        }
      }

      // Si mode Stripe → créer une session et rediriger
      if (form.setup_payment_method === "stripe") {
        try {
          const pay = await api.post("/agency/onboarding/pay-setup", {
            onboarding_request_id: request.id,
            installment_number: 1,
            origin_url: window.location.origin,
          });
          if (pay.data?.url) {
            window.location.href = pay.data.url;
            return;
          }
          toast.error("Lien de paiement Stripe indisponible");
        } catch (e) {
          toast.error("Stripe: " + (e?.response?.data?.detail || e.message));
        }
      }

      // Virement bancaire → on affiche la confirmation directement
      setSubmitted(request);
      setStep(5);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'envoi");
    } finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950">
      <div className="text-center">
        <div className="inline-block w-12 h-12 border-4 border-sky-500/30 border-t-sky-400 rounded-full animate-spin mb-3"/>
        <div className="text-slate-400 text-sm">Préparation de votre espace…</div>
      </div>
    </div>
  );

  const totalSteps = 4;
  const progress = step >= 5 ? 100 : Math.round(((step - 1) / totalSteps) * 100);
  // Messages d'encouragement par étape (gamification) — adapté selon le mode
  const ENCOURAGEMENTS = {
    1: { emoji: "🚀", title: "C'est parti !", desc: "Choisissez la formule qui vous correspond — vous pourrez ajuster plus tard." },
    2: { emoji: "💪", title: "Excellent choix !", desc: "Quelques infos pour personnaliser votre projet et préparer votre site." },
    3: { emoji: "📋", title: "On y est presque !", desc: "Validation rapide des conditions et signature pour officialiser." },
    4: isCustomQuote
      ? { emoji: "✉️", title: "Plus qu'à envoyer !", desc: "Notre équipe étudie votre demande et vous propose un devis personnalisé sous 24h." }
      : { emoji: "💳", title: "Dernière ligne droite !", desc: "Réglez les frais de setup et c'est parti — votre site démarre dans 5 jours." },
    5: isCustomQuote
      ? { emoji: "📨", title: "Demande envoyée !", desc: "Vous recevrez votre devis personnalisé sous 24h. Si vous l'acceptez, vous pourrez régler par CB ou virement." }
      : { emoji: "🎉", title: "Bravo !", desc: "Votre demande est envoyée. On vous recontacte sous 24h." },
  };
  const enc = ENCOURAGEMENTS[step] || ENCOURAGEMENTS[1];
  const agencyName = agencySettings.agency_brand_name || agencySettings.agency_name || "Agence MSC";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-sky-50/30 text-slate-900 relative overflow-hidden" data-testid="onboarding-public-page">
      {/* Background décoratif subtil — light mode */}
      <div className="fixed inset-0 opacity-40 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-sky-200/40 rounded-full blur-3xl"/>
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-violet-200/30 rounded-full blur-3xl"/>
        <div className="absolute top-1/2 left-1/2 w-[400px] h-[400px] bg-emerald-100/40 rounded-full blur-3xl"/>
      </div>

      {/* Header sticky : retour à gauche, logo WECOCKPIT à droite, progress bar dessous */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200/60 shadow-sm">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-900 transition group"
            data-testid="onboarding-back-home"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform"/>
            <span className="hidden sm:inline">Retour</span>
          </Link>
          <div className="flex items-center gap-3">
            <WeCockpitLogo variant="lockup" size={32} tone="dark" data-testid="onboarding-wecockpit-logo"/>
            <div className="hidden md:block border-l border-slate-200 pl-3">
              <div className="text-[9px] uppercase tracking-[0.18em] text-slate-400 font-bold">Solution opérée par</div>
              <div className="font-bold text-slate-700 text-xs">{agencyName}</div>
            </div>
          </div>
        </div>
        {/* Progress bar */}
        <div className="h-1 bg-slate-100 relative overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-sky-500 via-violet-500 to-fuchsia-500 transition-all duration-700 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      </header>

      <div className="relative max-w-4xl mx-auto px-4 py-8 sm:py-12">
        {/* Hero — message d'encouragement (style moderne, sans emoji géant) */}
        <div className="text-center mb-10">
          <div
            key={step}
            className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-sky-500 to-violet-500 text-white shadow-lg shadow-sky-500/30 mb-4"
            style={{ animation: "scaleIn 0.5s cubic-bezier(0.34,1.56,0.64,1)" }}
          >
            {step === 1 && <Rocket className="w-6 h-6"/>}
            {step === 2 && <Sparkles className="w-6 h-6"/>}
            {step === 3 && <FileText className="w-6 h-6"/>}
            {step === 4 && (isCustomQuote ? <Mail className="w-6 h-6"/> : <CreditCard className="w-6 h-6"/>)}
            {step === 5 && <Check className="w-6 h-6"/>}
          </div>
          <h1 className="text-3xl sm:text-4xl font-black mb-2 text-slate-900 tracking-tight">
            {enc.title}
          </h1>
          <p className="text-sm sm:text-base text-slate-500 max-w-xl mx-auto">{enc.desc}</p>
          <div className="mt-4 inline-flex items-center gap-2 text-xs text-slate-500 bg-white/60 backdrop-blur px-3 py-1.5 rounded-full border border-slate-200/60">
            <Trophy className="w-3.5 h-3.5 text-amber-500"/>
            Étape <span className="font-bold text-slate-900">{Math.min(step, totalSteps)}</span> sur {totalSteps} · <span className="font-bold text-emerald-600">{progress}%</span> complété
          </div>
        </div>

        {/* Stepper visuel — fond clair */}
        <div className="flex items-center justify-between mb-8 px-2 relative">
          <div className="absolute top-5 left-0 right-0 h-0.5 bg-slate-200 -z-10"/>
          <div className="absolute top-5 left-0 h-0.5 bg-gradient-to-r from-emerald-500 to-sky-500 -z-10 transition-all duration-700" style={{ width: `${progress}%` }}/>
          {["Forfait", "Coordonnées", "CGV & Signature", "Paiement"].map((lbl, i) => {
            const stepN = i + 1;
            const isActive = stepN === step;
            const isDone = stepN < step;
            return (
              <div key={lbl} className="flex-1 text-center">
                <div className={`w-11 h-11 mx-auto rounded-full flex items-center justify-center font-bold mb-2 transition-all duration-300 ${
                  isDone ? "bg-emerald-500 text-white shadow-md shadow-emerald-500/30 scale-100" :
                  isActive ? "bg-gradient-to-br from-sky-500 to-violet-500 text-white shadow-lg shadow-sky-500/40 scale-110 ring-4 ring-sky-500/15" :
                  "bg-white text-slate-400 border border-slate-200"
                }`}>
                  {isDone ? <CheckCircle2 className="w-5 h-5" /> : stepN}
                </div>
                <div className={`text-[11px] font-semibold ${isActive ? "text-slate-900" : isDone ? "text-emerald-600" : "text-slate-400"}`}>{lbl}</div>
              </div>
            );
          })}
        </div>

        <Card className="p-6 sm:p-8 bg-white border-slate-200/60 text-slate-900 shadow-xl shadow-slate-200/40 rounded-2xl">
          {step === 1 && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold mb-2 text-slate-900">Étape 1 — Choisissez votre forfait</h2>

              {presetLabel && form.plan_code && (
                <div className="bg-gradient-to-r from-sky-50 to-violet-50 border border-sky-200 rounded-xl p-4 flex items-start gap-3" data-testid="onb-preset-badge">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-500 to-violet-500 flex items-center justify-center shrink-0 shadow-md">
                    <Sparkles className="w-5 h-5 text-white"/>
                  </div>
                  <div className="flex-1">
                    <div className="text-xs uppercase tracking-wider font-bold text-sky-600">Pré-configuration appliquée</div>
                    <div className="font-bold text-slate-900 mt-0.5">{presetLabel}</div>
                    <div className="text-xs text-slate-600 mt-1">
                      Nous avons sélectionné le forfait <strong>{plans.find(p => p.code === form.plan_code)?.name}</strong>
                      {form.extra_widgets.length > 0 && <> et pré-coché <strong>{form.extra_widgets.length} widget{form.extra_widgets.length > 1 ? "s" : ""} optionnel{form.extra_widgets.length > 1 ? "s" : ""}</strong> adaptés à votre projet</>}.
                      Vous pouvez tout ajuster ci-dessous.
                    </div>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {plans.map(p => (
                  <Card key={p.code} className={`p-5 cursor-pointer transition-all rounded-xl ${form.plan_code === p.code ? "bg-gradient-to-br from-sky-50 to-violet-50 border-2 border-sky-400 shadow-lg shadow-sky-200/40 scale-[1.02]" : "bg-white border border-slate-200 hover:border-sky-300 hover:shadow-md"}`} onClick={() => setForm({ ...form, plan_code: p.code })} data-testid={`plan-option-${p.code}`}>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-bold text-lg text-slate-900">{p.name}</h3>
                      {form.plan_code === p.code && <CheckCircle2 className="w-5 h-5 text-sky-500" />}
                    </div>
                    <div className="text-2xl font-bold text-slate-900 mb-1">{cents(p.monthly_price_cents)}<span className="text-sm text-slate-500 font-normal">/mois</span></div>
                    <div className="text-xs text-slate-500">+ setup {cents(p.setup_fee_cents)} ({p.setup_fee_installments_max}×)</div>
                    <p className="text-xs mt-3 text-slate-600 leading-relaxed">{p.description}</p>
                    <div className="mt-3 space-y-1 text-xs text-slate-700">
                      <div>✓ {p.included_widgets.length} widgets inclus</div>
                      {p.trial_days > 0 && <div>✓ {p.trial_days} jours d'essai</div>}
                      <div>✓ {p.ai_credits_monthly} crédits IA/mois</div>
                      <div>✓ {p.support_hours_monthly}h support/mois</div>
                    </div>
                  </Card>
                ))}
              </div>

              {widgets.filter(w => w.is_payable).length > 0 && (
                <div className="pt-5 mt-2 border-t border-slate-200">
                  <h3 className="font-bold mb-3 text-slate-900 text-sm">Widgets supplémentaires (optionnel)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {widgets.filter(w => w.is_payable).map(w => {
                      const isIncluded = selectedPlan?.included_widgets?.includes(w.widget_key);
                      const checked = form.extra_widgets.includes(w.widget_key);
                      return (
                        <label key={w.widget_key} className={`flex items-start gap-2.5 p-3 rounded-lg border transition ${isIncluded ? "bg-emerald-50 border-emerald-200 opacity-80" : "bg-white border-slate-200 cursor-pointer hover:border-sky-400 hover:shadow-sm"}`}>
                          <Checkbox checked={isIncluded || checked} disabled={isIncluded} onCheckedChange={v => {
                            if (isIncluded) return;
                            setForm({ ...form, extra_widgets: v ? [...form.extra_widgets, w.widget_key] : form.extra_widgets.filter(x => x !== w.widget_key) });
                          }} />
                          <div className="flex-1">
                            <div className="font-medium text-sm text-slate-900">{w.label} {isIncluded && <Badge className="bg-emerald-500 ml-1 text-[10px]">Inclus</Badge>}</div>
                            <div className="text-xs text-slate-500 mt-0.5">{w.description}</div>
                            {!isIncluded && <div className="text-xs text-sky-600 mt-1 font-semibold">+{cents(w.monthly_price_cents)}/mois {w.discount_pct > 0 && <span className="text-rose-500">(-{w.discount_pct}%)</span>}</div>}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-slate-900">Étape 2 — Vos coordonnées</h2>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Entreprise *</Label><Input className="bg-white border-slate-200 focus:border-sky-400 focus:ring-2 focus:ring-sky-100" value={form.company_name} onChange={e => setForm({ ...form, company_name: e.target.value })} data-testid="onb-company" /></div>
                <div><Label>SIRET</Label><Input className="bg-white border-slate-200 focus:border-sky-400 focus:ring-2 focus:ring-sky-100" value={form.siret} onChange={e => setForm({ ...form, siret: e.target.value })} /></div>
                <div><Label>Prénom *</Label><Input className="bg-white border-slate-200 focus:border-sky-400 focus:ring-2 focus:ring-sky-100" value={form.contact_first_name} onChange={e => setForm({ ...form, contact_first_name: e.target.value })} data-testid="onb-firstname" /></div>
                <div><Label>Nom</Label><Input className="bg-white border-slate-200 focus:border-sky-400 focus:ring-2 focus:ring-sky-100" value={form.contact_last_name} onChange={e => setForm({ ...form, contact_last_name: e.target.value })} /></div>
                <div><Label>Email *</Label><Input className="bg-white border-slate-200 focus:border-sky-400 focus:ring-2 focus:ring-sky-100" type="email" value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })} data-testid="onb-email" /></div>
                <div><Label>Téléphone *</Label><Input className="bg-white border-slate-200 focus:border-sky-400 focus:ring-2 focus:ring-sky-100" value={form.contact_phone} onChange={e => setForm({ ...form, contact_phone: e.target.value })} data-testid="onb-phone" /></div>
              </div>
              <div><Label>Adresse</Label><Input className="bg-white border-slate-200 focus:border-sky-400 focus:ring-2 focus:ring-sky-100" value={form.billing_address} onChange={e => setForm({ ...form, billing_address: e.target.value })} /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>CP</Label><Input className="bg-white border-slate-200 focus:border-sky-400 focus:ring-2 focus:ring-sky-100" value={form.billing_zip} onChange={e => setForm({ ...form, billing_zip: e.target.value })} /></div>
                <div className="col-span-2"><Label>Ville</Label><Input className="bg-white border-slate-200 focus:border-sky-400 focus:ring-2 focus:ring-sky-100" value={form.billing_city} onChange={e => setForm({ ...form, billing_city: e.target.value })} /></div>
              </div>
              <div><Label>Secteur d'activité</Label><Input className="bg-white border-slate-200 focus:border-sky-400 focus:ring-2 focus:ring-sky-100" value={form.sector_preset} onChange={e => setForm({ ...form, sector_preset: e.target.value })} placeholder="boulangerie, plombier, avocat…" /></div>
              <div><Label>Remarques / attentes</Label><Textarea className="bg-white border-slate-200 focus:border-sky-400 focus:ring-2 focus:ring-sky-100" rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
          )}

          {step === 3 && cgv && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold flex items-center gap-2 text-slate-900"><FileCheck2 className="w-5 h-5 text-sky-500" />Étape 3 — {cgv.title} ({cgv.version})</h2>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 max-h-80 overflow-y-auto text-sm prose prose-sm" dangerouslySetInnerHTML={{ __html: cgv.content_html }} />
              <div className="space-y-3 pt-2">
                <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg hover:bg-slate-50 transition">
                  <Checkbox checked={form.cgv_accepted} onCheckedChange={v => setForm({ ...form, cgv_accepted: !!v })} data-testid="onb-cgv-accept" />
                  <span className="text-sm text-slate-700">J'ai lu et j'accepte les Conditions Générales de Vente version <strong className="text-slate-900">{cgv.version}</strong> en vigueur.</span>
                </label>
                <div>
                  <Label className="text-slate-700">Signature (tapez votre nom complet) *</Label>
                  <Input className="bg-white border-slate-300 focus:border-sky-400 focus:ring-2 focus:ring-sky-100 font-serif italic text-lg" placeholder="Jean Dupont" value={form.signed_name} onChange={e => setForm({ ...form, signed_name: e.target.value })} data-testid="onb-signature" />
                </div>
              </div>
            </div>
          )}

          {step === 4 && selectedPlan && (
            <div className="space-y-4">
              {isCustomQuote ? (
                <>
                  <h2 className="text-xl font-bold flex items-center gap-2 text-violet-600"><FileCheck2 className="w-5 h-5"/>Étape 4 — Demande de devis personnalisé</h2>
                  <Card className="p-5 bg-gradient-to-br from-violet-50 to-fuchsia-50 border-violet-200">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center shrink-0 shadow-md">
                        <Sparkles className="w-6 h-6 text-white"/>
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900 text-lg mb-2">Pas de paiement immédiat</h3>
                        <p className="text-sm text-slate-700 leading-relaxed mb-3">
                          Votre projet sur-mesure mérite un devis personnalisé. Voici comment ça se passe :
                        </p>
                        <ol className="space-y-2 text-sm text-slate-700">
                          <li className="flex gap-2"><span className="font-bold text-violet-600">1.</span><span>Vous envoyez votre demande maintenant — <strong>aucun paiement</strong>.</span></li>
                          <li className="flex gap-2"><span className="font-bold text-violet-600">2.</span><span>Notre équipe vous propose un <strong>devis personnalisé sous 24h</strong> (frais de setup adaptés à votre projet).</span></li>
                          <li className="flex gap-2"><span className="font-bold text-violet-600">3.</span><span>Si vous acceptez, vous payez par <strong>CB (Stripe) ou virement</strong> — au choix.</span></li>
                          <li className="flex gap-2"><span className="font-bold text-violet-600">4.</span><span>Démarrage de la création de votre site dès réception.</span></li>
                        </ol>
                      </div>
                    </div>
                  </Card>
                  <Card className="p-4 bg-slate-50 border-slate-200">
                    <div className="text-xs text-slate-500 uppercase tracking-wider font-bold mb-2">Récapitulatif de votre demande</div>
                    <div className="space-y-1 text-sm text-slate-700">
                      <div className="flex justify-between"><span>Type de site</span><strong>Sur-mesure / Devis</strong></div>
                      <div className="flex justify-between"><span>Forfait base souhaité</span><strong>{selectedPlan.name}</strong></div>
                      {form.extra_widgets.length > 0 && (
                        <div className="flex justify-between text-sky-600"><span>Widgets souhaités</span><strong>{form.extra_widgets.length}</strong></div>
                      )}
                      {form.notes && (
                        <div className="text-xs text-slate-500 italic mt-2 pt-2 border-t border-slate-200">"{form.notes.slice(0, 200)}..."</div>
                      )}
                    </div>
                  </Card>
                  <div className="flex gap-2 pt-2">
                    <Button variant="outline" onClick={() => setStep(3)} className="border-slate-300 text-slate-700"><ArrowLeft className="w-4 h-4 mr-2"/>Retour</Button>
                    <Button
                      onClick={() => submitRequest(true)}
                      disabled={submitting}
                      className="bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 text-white font-bold flex-1 shadow-md shadow-violet-200"
                      data-testid="submit-quote-request"
                    >
                      {submitting ? "Envoi…" : <>Envoyer ma demande de devis<ArrowRight className="w-4 h-4 ml-2"/></>}
                    </Button>
                  </div>
                </>
              ) : (
                <>
              <h2 className="text-xl font-bold flex items-center gap-2 text-slate-900"><CreditCard className="w-5 h-5 text-sky-500" />Étape 4 — Paiement des frais de setup</h2>
              <Card className="p-4 bg-slate-50 border-slate-200">
                <h3 className="font-bold mb-2 text-slate-900">Récapitulatif</h3>
                <div className="space-y-1 text-sm text-slate-700">
                  <div className="flex justify-between"><span>Forfait {selectedPlan.name}</span><strong>{cents(selectedPlan.monthly_price_cents)}/mois</strong></div>
                  {form.extra_widgets.length > 0 && (
                    <div className="flex justify-between text-sky-600"><span>+ {form.extra_widgets.length} widget(s) optionnels</span><strong>+{cents(totalExtra)}/mois</strong></div>
                  )}
                  <div className="flex justify-between pt-2 border-t border-slate-200">
                    <strong>Frais de setup (aujourd'hui)</strong>
                    {promoApplied ? (
                      <div className="text-right">
                        <div className="line-through text-slate-400 text-xs">{cents(selectedPlan.setup_fee_cents)}</div>
                        <strong className="text-emerald-600">
                          {cents(Math.round(selectedPlan.setup_fee_cents * (100 - promoApplied.discount_percent) / 100))}
                          {promoApplied.discount_percent === 100 && " · GRATUIT"}
                        </strong>
                      </div>
                    ) : (
                      <strong className="text-sky-600">{cents(selectedPlan.setup_fee_cents)}</strong>
                    )}
                  </div>
                </div>
              </Card>

              {/* Code promo */}
              <Card className="p-4 bg-white border-slate-200" data-testid="promo-block">
                <div className="flex items-center gap-2 mb-2">
                  <Tag className="w-4 h-4 text-sky-500" />
                  <h3 className="font-bold text-sm text-slate-900">Code promo</h3>
                </div>
                {promoApplied ? (
                  <div className="flex items-center justify-between gap-2 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                    <div className="min-w-0">
                      <div className="font-mono font-bold text-emerald-600">{promoApplied.code}</div>
                      <div className="text-xs text-slate-600 truncate">
                        -{promoApplied.discount_percent}% appliqué{promoApplied.description ? ` · ${promoApplied.description}` : ""}
                      </div>
                    </div>
                    <Button size="sm" variant="ghost" onClick={removePromo} data-testid="promo-remove" className="text-slate-500 hover:text-rose-600">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      className="bg-white border-slate-200 focus:border-sky-400 focus:ring-2 focus:ring-sky-100 font-mono uppercase"
                      placeholder="Ex: MSC-FREE-100"
                      value={promoInput}
                      onChange={e => setPromoInput(e.target.value.toUpperCase())}
                      onKeyDown={e => e.key === "Enter" && checkPromo()}
                      data-testid="promo-input"
                    />
                    <Button onClick={checkPromo} disabled={promoChecking || !promoInput.trim()} variant="outline" data-testid="promo-apply-btn">
                      {promoChecking ? "…" : "Appliquer"}
                    </Button>
                  </div>
                )}
              </Card>

              {/* Mode de paiement (masqué si gratuit total) */}
              {!(promoApplied && promoApplied.discount_percent === 100) && (
              <>
              <div>
                <Label className="text-slate-700">Mode de paiement</Label>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <Card className={`p-3 cursor-pointer flex items-center gap-2 transition ${form.setup_payment_method === "stripe" ? "bg-sky-50 border-sky-400 ring-2 ring-sky-200" : "bg-white border-slate-200 hover:border-slate-300"}`} onClick={() => setForm({ ...form, setup_payment_method: "stripe" })}>
                    <CreditCard className="w-4 h-4 text-sky-500" />Carte bancaire (Stripe)
                  </Card>
                  <Card className={`p-3 cursor-pointer flex items-center gap-2 transition ${form.setup_payment_method === "bank_transfer" ? "bg-sky-50 border-sky-400 ring-2 ring-sky-200" : "bg-white border-slate-200 hover:border-slate-300"}`} onClick={() => setForm({ ...form, setup_payment_method: "bank_transfer" })}>
                    <Banknote className="w-4 h-4 text-sky-500" />Virement bancaire
                  </Card>
                </div>
              </div>
              <div>
                <Label className="text-slate-700">Nombre d'échéances (max {selectedPlan.setup_fee_installments_max})</Label>
                <div className="flex gap-2 mt-2 flex-wrap">
                  {[1, 2, 3, 4].filter(n => n <= selectedPlan.setup_fee_installments_max).map(n => (
                    <Button key={n} variant={form.setup_installments === n ? "default" : "outline"} onClick={() => setForm({ ...form, setup_installments: n })} data-testid={`onb-installments-${n}`}>
                      {n}× {cents(Math.ceil(selectedPlan.setup_fee_cents / n))}
                    </Button>
                  ))}
                </div>
              </div>
              </>
              )}
              {(promoApplied && promoApplied.discount_percent === 100) && (
                <Card className="p-4 bg-emerald-50 border-emerald-200">
                  <div className="text-sm text-emerald-700">
                    🎉 Code <strong className="font-mono">{promoApplied.code}</strong> · création <strong>gratuite</strong>. Aucun paiement n'est requis. Validez la demande pour finaliser.
                  </div>
                </Card>
              )}
              </>
              )}
            </div>
          )}

          {step === 5 && submitted && (
            <div className="text-center space-y-5 py-10 relative">
              {/* Confettis animés */}
              <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
                {Array.from({ length: 30 }).map((_, i) => (
                  <div
                    key={i}
                    className="absolute w-2 h-2 rounded-sm"
                    style={{
                      left: `${Math.random() * 100}%`,
                      top: "-10px",
                      backgroundColor: ["#3DB9E8", "#10B981", "#F59E0B", "#EC4899", "#8B5CF6"][i % 5],
                      animation: `fall ${3 + Math.random() * 2}s ease-in ${Math.random() * 1}s forwards`,
                      transform: `rotate(${Math.random() * 360}deg)`,
                    }}
                  />
                ))}
              </div>
              <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-emerald-400 to-sky-500 flex items-center justify-center shadow-2xl shadow-emerald-500/50 relative" style={{ animation: "scaleIn 0.7s cubic-bezier(0.34,1.56,0.64,1)" }}>
                <PartyPopper className="w-12 h-12 text-white" />
                <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-amber-400 flex items-center justify-center text-lg" style={{ animation: "scaleIn 1s cubic-bezier(0.34,1.56,0.64,1) 0.3s both" }}>
                  ✨
                </div>
              </div>
              <h2 className="text-3xl font-black text-slate-900">
                Bravo, votre projet est lancé !
              </h2>
              <p className="text-slate-600 max-w-md mx-auto">Votre demande est bien arrivée. Notre équipe vous recontacte sous <strong className="text-slate-900">24h</strong> pour finaliser et démarrer la création de votre site.</p>
              <div className="bg-white p-5 rounded-2xl text-left max-w-md mx-auto border border-slate-200 shadow-sm">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Référence</div>
                    <div className="font-mono text-xs text-emerald-600 truncate">{submitted.id?.slice(0, 12)}…</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Forfait</div>
                    <div className="font-bold text-slate-900">{submitted.plan_snapshot?.name || submitted.plan_code}</div>
                  </div>
                  <div className="col-span-2">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Setup</div>
                    <div className="font-bold text-lg text-slate-900">{cents(submitted.setup_fee_total_cents)} <span className="text-xs text-slate-500 font-normal">({submitted.setup_installments}×, {submitted.setup_payment_method === "stripe" ? "CB" : submitted.setup_payment_method === "promo_free" ? "Offert ✨" : "Virement"})</span></div>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 justify-center">
                <Button onClick={() => nav("/")} className="bg-gradient-to-r from-sky-500 to-violet-500 hover:from-sky-600 hover:to-violet-600 text-white font-bold shadow-md shadow-sky-200">
                  <Rocket className="w-4 h-4 mr-2"/>Retour à l'accueil
                </Button>
              </div>
            </div>
          )}

          {step < 5 && (
            <div className="flex justify-between pt-6 mt-6 border-t border-slate-200">
              <Button variant="outline" disabled={step === 1} onClick={prev} className="text-slate-700 border-slate-300"><ArrowLeft className="w-4 h-4 mr-1" />Précédent</Button>
              {step < 4 ? (
                <Button onClick={next} data-testid={`onb-next-step-${step}`} className="bg-gradient-to-r from-sky-500 to-violet-500 hover:from-sky-600 hover:to-violet-600 text-white shadow-md shadow-sky-200">Suivant<ArrowRight className="w-4 h-4 ml-1" /></Button>
              ) : (
                <Button onClick={submit} disabled={submitting} data-testid="onb-submit-btn" className="bg-gradient-to-r from-emerald-500 to-sky-500 hover:from-emerald-600 hover:to-sky-600 text-white shadow-md shadow-emerald-200">{submitting ? "Envoi…" : "Valider la demande"}<ArrowRight className="w-4 h-4 ml-1" /></Button>
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
